import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Button,
  Stack,
  Paper,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptIcon from '@mui/icons-material/Receipt';
import dayjs from 'dayjs';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import { useGetCashDocumentByIdQuery } from '../Service/cashDocumentsApi';
import { useLazyGetCashDocumentPdfQuery } from '../Service/extendedCashDocumentsApi';
import {
  formatAmount,
  getTypeLabel,
  getTypeColor,
  CashDocumentType,
} from '../Types/cashDocumentTypes';
import { getCashDocumentsErrorMessage } from '../Utils/cashDocumentsErrorMapping';
import { downloadCashDocumentPdf } from '../Utils/cashDocumentPdfUtils';

/**
 * Detail page for viewing a single Cash Document
 * Shows all fields + PDF preview/download
 */
const CashDocumentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const documentId = Number(id);
  const [triggerGetCashDocumentPdf] = useLazyGetCashDocumentPdfQuery();

  const {
    data: cashDocument,
    isLoading,
    error,
  } = useGetCashDocumentByIdQuery(documentId, {
    skip: !documentId,
  });

  const handleDownloadPdf = async () => {
    if (!cashDocument) return;
    try {
      await downloadCashDocumentPdf(
        triggerGetCashDocumentPdf,
        documentId,
        `${cashDocument.documentNumber}.pdf`
      );
    } catch (err) {
      console.error('PDF download failed:', err);
      alert(getCashDocumentsErrorMessage(err, 'Nie udało się pobrać PDF. Spróbuj ponownie.'));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !cashDocument) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {getCashDocumentsErrorMessage(
          error,
          'Nie znaleziono dokumentu kasowego.'
        )}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header with actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon color="primary" fontSize="large" />
          Dokument kasowy: {cashDocument.documentNumber}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/cash-documents')}
          >
            Wróć
          </Button>
          <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleDownloadPdf}>
            Pobierz PDF
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/cash-documents/edit/${documentId}`)}
          >
            Edytuj
          </Button>
        </Stack>
      </Box>

      <CustomCard title="Szczegóły" icon={<ReceiptIcon color="primary" />}>
        <Stack spacing={3}>
          {/* Header Info */}
          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }} >
                <Typography variant="body2" color="text.secondary">
                  Typ dokumentu
                </Typography>
                <Chip
                  label={getTypeLabel(cashDocument.type)}
                  color={getTypeColor(cashDocument.type)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }} >
                <Typography variant="body2" color="text.secondary">
                  Kwota
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  color={cashDocument.type === CashDocumentType.KP ? 'success.main' : 'error.main'}
                >
                  {formatAmount(cashDocument.amount, cashDocument.currency)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Divider />

          {/* Document Details */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }} >
              <Typography variant="body2" color="text.secondary">
                Data dokumentu
              </Typography>
              <Typography variant="body1">
                {cashDocument.documentDate
                  ? dayjs(cashDocument.documentDate).format('DD.MM.YYYY')
                  : '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} >
              <Typography variant="body2" color="text.secondary">
                Waluta
              </Typography>
              <Typography variant="body1">{cashDocument.currency}</Typography>
            </Grid>
            <Grid size={{ xs: 12 }} >
              <Typography variant="body2" color="text.secondary">
                Osoba
              </Typography>
              <Typography variant="body1">{cashDocument.personName}</Typography>
            </Grid>
            {cashDocument.personAddress && (
              <Grid size={{ xs: 12 }} >
                <Typography variant="body2" color="text.secondary">
                  Adres
                </Typography>
                <Typography variant="body1">{cashDocument.personAddress}</Typography>
              </Grid>
            )}
            {cashDocument.description && (
              <Grid size={{ xs: 12 }} >
                <Typography variant="body2" color="text.secondary">
                  Opis
                </Typography>
                <Typography variant="body1">{cashDocument.description}</Typography>
              </Grid>
            )}
          </Grid>

          <Divider />

          {/* Related Entities */}
          <Grid container spacing={2}>
            {cashDocument.reservationId && (
              <Grid size={{ xs: 12, sm: 6 }} >
                <Typography variant="body2" color="text.secondary">
                  Rezerwacja
                </Typography>
                <Chip
                  label={`Rezerwacja #${cashDocument.reservationId}`}
                  color="primary"
                  variant="outlined"
                  onClick={() => navigate(`/reservations/update/${cashDocument.reservationId}`)}
                  sx={{ cursor: 'pointer' }}
                />
              </Grid>
            )}
            {cashDocument.invoiceId && (
              <Grid size={{ xs: 12, sm: 6 }} >
                <Typography variant="body2" color="text.secondary">
                  Faktura
                </Typography>
                <Chip
                  label={`Faktura #${cashDocument.invoiceId}`}
                  color="primary"
                  variant="outlined"
                  onClick={() => navigate(`/invoices/update/${cashDocument.invoiceId}`)}
                  sx={{ cursor: 'pointer' }}
                />
              </Grid>
            )}
          </Grid>

          {/* Metadata */}
          <Divider />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }} >
              <Typography variant="caption" color="text.secondary">
                Utworzono: {dayjs(cashDocument.createdAt).format('DD.MM.YYYY HH:mm')}
              </Typography>
            </Grid>
            {cashDocument.updatedAt && (
              <Grid size={{ xs: 12, sm: 6 }} >
                <Typography variant="caption" color="text.secondary">
                  Zaktualizowano: {dayjs(cashDocument.updatedAt).format('DD.MM.YYYY HH:mm')}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Stack>
      </CustomCard>
    </Box>
  );
};

export default CashDocumentDetailPage;
